import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { templatesAPI, projectsAPI } from '../services/api';
import useDesktopCheck from '../hooks/useDesktopCheck';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Button from '../components/UI/Button';
import JustifiedGrid from '../components/UI/JustifiedGrid';
import {
  FileVideo,
  Play,
  Pause,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Individual template card with description overlay
const TemplateGridItem = ({ template, playingVideo, videoRefs, onPlayVideo, onUseTemplate, isCreatingProject }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const descRef = useRef(null);

  const checkTruncation = useCallback((el) => {
    if (el) {
      descRef.current = el;
      requestAnimationFrame(() => {
        setNeedsTruncation(el.scrollHeight > el.clientHeight);
      });
    }
  }, []);

  const description = template.description || '';

  return (
    <div className="group relative rounded-xl overflow-hidden cursor-pointer bg-ag-black-900 w-full h-full">
      {/* Media */}
      {playingVideo === template._id && template.previewVideoUrl ? (
        <video
          ref={el => videoRefs.current[template._id] = el}
          className="absolute inset-0 w-full h-full object-cover"
          src={template.previewVideoUrl}
          loop
          playsInline
          autoPlay
        />
      ) : template.thumbnailUrl ? (
        <img
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          src={template.thumbnailUrl}
          alt={template.name}
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-ag-black-800">
          <FileVideo className="text-gray-600" size={48} />
        </div>
      )}

      {/* Play button */}
      {template.previewVideoUrl && (
        <button
          onClick={(e) => onPlayVideo(e, template._id, template.previewVideoUrl)}
          className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white flex items-center justify-center transition-all hover:scale-110 z-20"
          aria-label={playingVideo === template._id ? "Pause preview" : "Play preview"}
        >
          {playingVideo === template._id ? (
            <Pause size={14} fill="currentColor" />
          ) : (
            <Play size={14} fill="currentColor" className="ml-0.5" />
          )}
        </button>
      )}

      {/* Hover overlay with Use Template button */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
        <Button
          size="sm"
          className="bg-ag-green text-ag-black-800 hover:bg-ag-green/90 border-0 font-semibold"
          onClick={() => onUseTemplate(template)}
          disabled={isCreatingProject}
        >
          {isCreatingProject ? 'Creating...' : 'Use Template'}
        </Button>
      </div>

      {/* Bottom gradient + name/description overlay - only visible on hover */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-10 px-3 pb-3">
          <p className="text-sm font-medium text-white truncate pointer-events-auto">
            {template.name}
          </p>
          {description && (
            <>
              <p
                ref={checkTruncation}
                className="text-xs text-white/70 leading-relaxed mt-1 pointer-events-auto"
                style={!expanded ? {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                } : undefined}
              >
                {description}
              </p>
              {needsTruncation && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                  className="text-[10px] text-white/50 hover:text-white/80 mt-1 flex items-center gap-0.5 pointer-events-auto transition-colors"
                >
                  {expanded ? (
                    <>Show less <ChevronUp size={10} /></>
                  ) : (
                    <>Show more <ChevronDown size={10} /></>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplatesPage = () => {
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRefs = useRef({});

  const isMobileOrPortrait = useDesktopCheck();

  useEffect(() => {
    if (playingVideo && videoRefs.current[playingVideo]) {
      const video = videoRefs.current[playingVideo];
      video.play().catch(err => console.log('Video play failed:', err));
    }
  }, [playingVideo]);

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['publicTemplates'],
    queryFn: templatesAPI.getAll
  });

  const handleUseTemplate = async (template) => {
    if (isMobileOrPortrait) {
      toast.error('Creating projects requires a desktop or laptop with a landscape display. Please switch to a larger device to continue.', {
        duration: 5000,
        icon: '💻',
      });
      return;
    }

    setIsCreatingProject(true);

    try {
      const layersWithoutIds = (template.layers || []).map(layer => {
        const { id, ...layerWithoutId } = layer;
        return layerWithoutId;
      });

      const projectData = {
        name: template.name || 'Untitled Project',
        canvas: {
          width: template.canvas?.width || 1920,
          height: template.canvas?.height || 1080,
          name: template.canvas?.name || `${template.canvas?.width || 1920} × ${template.canvas?.height || 1080}`,
          backgroundColor: template.canvas?.backgroundColor || '#000000'
        },
        duration: 10,
        layers: layersWithoutIds
      };

      const projectCreated = await projectsAPI.createNewProject(projectData);

      if (projectCreated?.data?.status === 'success') {
        const projectId = projectCreated.data.project._id;
        window.open(`/lite-view/${projectId}`);
      } else {
        alert('Error creating project. Please try again.');
      }
    } catch (error) {
      console.error('Error creating project for template:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handlePlayVideo = (e, templateId, previewUrl) => {
    e.stopPropagation();
    if (!previewUrl) return;

    if (playingVideo === templateId) {
      if (videoRefs.current[templateId]) {
        videoRefs.current[templateId].pause();
        videoRefs.current[templateId].currentTime = 0;
      }
      setPlayingVideo(null);
    } else {
      if (playingVideo && videoRefs.current[playingVideo]) {
        videoRefs.current[playingVideo].pause();
        videoRefs.current[playingVideo].currentTime = 0;
      }
      setPlayingVideo(templateId);
    }
  };

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const templates = templatesData?.data?.data?.templates || templatesData?.data?.templates || [];

  return (
    <div className="min-h-screen bg-ag-black-800 p-4 sm:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Templates</h1>
          <p className="text-gray-400 mt-1 text-sm">Choose a template to start your project</p>
        </div>
        <span className="text-sm text-gray-500">{templates.length} templates</span>
      </div>

      {/* Justified Row Gallery */}
      {templates.length === 0 ? (
        <div className="text-center py-16">
          <FileVideo className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-medium text-white mb-2">No templates available</h3>
          <p className="text-gray-400">Check back later for new templates</p>
        </div>
      ) : (
        <JustifiedGrid
          items={templates}
          targetRowHeight={300}
          gap={6}
          getAspectRatio={(template) => (template.canvas?.width || 1920) / (template.canvas?.height || 1080)}
          keyExtractor={(template) => template._id}
          renderItem={(template) => (
            <TemplateGridItem
              template={template}
              playingVideo={playingVideo}
              videoRefs={videoRefs}
              onPlayVideo={handlePlayVideo}
              onUseTemplate={handleUseTemplate}
              isCreatingProject={isCreatingProject}
            />
          )}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
